import type { ReactNode } from 'react';

/**
 * The frame for sign-in and registration. Deliberately outside the app shell —
 * there is no navigation to offer someone who is not signed in.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    /*
      The safe-area inset is added to the top padding rather than replacing it,
      so it composes instead of overriding.

      It resolves to zero today: the iOS status bar is an opaque `black` (see
      index.html), so the web view never extends under it. Kept anyway — this
      screen is outside AppShell and nothing else clears the notch for it, so if
      that meta ever becomes translucent again this is already correct rather
      than a bug report about a hidden heading.
    */
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg px-4 py-10 pt-[calc(2.5rem+env(safe-area-inset-top))]">
      <div className="w-full max-w-sm">
        <header className="pb-6">
          {/*
            The wordmark, set as the design specifies it: Newsreader at the
            strong weight with tight tracking, one word, no space. This is the
            first screen anyone sees and the only one that carries the name, so
            it is the one place the wordmark actually appears in the app.
          */}
          <p className="pb-6 font-serif text-xl font-strong tracking-tight text-muted">
            ICULogbook
          </p>
          <h1 className="font-serif text-3xl font-strong tracking-tight text-text">{title}</h1>
          {subtitle === undefined ? null : <p className="pt-1 text-sm text-muted">{subtitle}</p>}
        </header>

        {children}

        {footer === undefined ? null : <div className="pt-6 text-sm text-muted">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * A form-level error, distinct from a field error. Focusable and announced, so
 * a failed submission is not silent for someone using a screen reader.
 */
export function FormError({ message }: { message: string | null }) {
  if (message === null) return null;

  return (
    <p
      role="alert"
      tabIndex={-1}
      className="rounded-md border border-danger bg-danger-quiet px-3 py-2 text-sm text-danger"
    >
      {message}
    </p>
  );
}
